/**
 * Sales Quotation page — /sales-quotations
 *
 * Feb 2026 — dedicated Sales workspace.
 *
 *  ┌─────────────────────────────────────────────────────────────┐
 *  │  📋 Ready for Quotation                                     │
 *  │     (comparisons fully approved that don't yet have an SQ)   │
 *  ├─────────────────────────────────────────────────────────────┤
 *  │  📑 My Quotations                                            │
 *  │     (list of all sales quotations + status badges)           │
 *  └─────────────────────────────────────────────────────────────┘
 *
 * Click a "Ready" row → POST /sales-quotations/from-comparison/{id} → opens detail
 * Click a "My Quotations" row → opens detail modal (read or edit)
 *
 * State machine displayed:
 *   draft → pending_sales_review → approved → sent_to_employee → sent_to_customer
 *                             ↓
 *                          rejected
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { FileText, CheckCircle, Plus, Trash2, Send, RefreshCw, History, ThumbsUp, FileSignature } from 'lucide-react';

const STATUS_TONE = {
  draft:                 'bg-gray-100 text-gray-700',
  pending_sales_review:  'bg-amber-100 text-amber-900',
  approved:              'bg-teal-200 text-teal-900',
  sent_to_employee:      'bg-indigo-200 text-indigo-900',
  sent_to_customer:      'bg-green-600 text-white',
  customer_accepted:     'bg-emerald-700 text-white',
  rejected:              'bg-rose-200 text-rose-900',
  cancelled:             'bg-rose-100 text-rose-800',
};

export default function SalesQuotations() {
  const [ready,  setReady]  = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalCfg, setApprovalCfg] = useState(null);
  const [opening, setOpening] = useState(null);
  const [viewing, setViewing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axiosInstance.get('/sales-quotations/ready'),
        axiosInstance.get('/sales-quotations?limit=300'),
        axiosInstance.get('/sales-quotations/meta/approval-config'),
        axiosInstance.get('/sales-quotations/meta/reopen-config'),
      ]);
      setReady(r1.data?.ready_for_quotation || []);
      setQuotes(r2.data?.quotations || []);
      setApprovalCfg({ ...(r3.data || {}), ...(r4.data || {}) });
    } catch (_) {
      toast.error('Failed to load Sales Quotations');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openFromComparison = async (comparison_id) => {
    setOpening(comparison_id);
    try {
      const r = await axiosInstance.post(`/sales-quotations/from-comparison/${comparison_id}`);
      toast.success(`Quote ${r.data?.quote_no} ready`);
      await load();
      setViewing(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Could not create quotation');
    } finally { setOpening(null); }
  };

  const openDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/sales-quotations/${id}`);
      setViewing(r.data);
    } catch (_) { toast.error('Failed to load'); }
  };

  const refreshDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/sales-quotations/${id}`);
      setViewing(r.data);
      load();
    } catch (_) { /* ignore — leave detail open */ }
  };

  // Quick-action: submit a draft for approval directly from the list row
  // (no modal needed). Closes the UX loop the user asked for.
  const submitFromRow = async (id) => {
    try {
      await axiosInstance.post(`/sales-quotations/${id}/submit`);
      toast.success('Submitted — manager has been notified');
      toast.message('Opening /approvals…');
      setTimeout(() => { window.location.assign('/approvals'); }, 700);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Submit failed');
    }
  };

  // Quick-action: mark sent_to_customer → customer_accepted from the row.
  const markAcceptedFromRow = async (id) => {
    try {
      await axiosInstance.post(`/sales-quotations/${id}/mark-accepted`);
      toast.success('Marked as Customer Accepted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Mark-accepted failed');
    }
  };

  // Quick-action: spin up a Contract draft from a customer-accepted SQ.
  // Idempotent — the SQ is stamped with contract_id after the first call.
  const createContractFromRow = async (id) => {
    try {
      const r = await axiosInstance.post(`/contracts/from-quotation/${id}`);
      const c = r.data?.contract;
      const wasIdempotent = r.data?.idempotent;
      toast.success(wasIdempotent
        ? `Already linked → ${c?.contract_number}`
        : `Contract ${c?.contract_number} created · opening Contracts…`);
      load();
      // Small delay so the toast is visible before we leave the page.
      setTimeout(() => navigate('/contracts'), 700);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                || e.response?.data?.detail?.error
                || 'Failed to create Contract');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sales-quotations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" /> Sales Quotation Workspace
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            عروض الأسعار المعتمدة للعملاء — تُنشأ تلقائياً من المقارنات المعتمدة (Procurement + Finance).
          </p>
        </div>
        <button onClick={load} data-testid="sales-quotations-refresh"
                 className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* ──── Ready for Quotation (top section) — RED URGENCY when items pending ──── */}
      {/* Visible only to Sales Reps + Admins / Managers — the people who actually
          act on the red row. Other roles (accountants, viewers) don't need to
          see the alert and would be confused by the urgency styling. */}
      {(['sales_rep','admin','pilot_admin','manager'].includes(approvalCfg?.current_user_role)) && (
      <section className={`border-2 ${ready.length > 0 ? 'border-rose-300 bg-rose-50/40' : 'border-dashed border-indigo-200 bg-indigo-50/40'} rounded-lg overflow-hidden`}
                data-testid="sales-quotations-ready-section">
        <div className={`px-4 py-2.5 border-b flex items-center justify-between ${ready.length > 0 ? 'bg-rose-100 border-rose-200' : 'bg-indigo-100 border-indigo-200'}`}>
          <h2 className={`text-sm font-bold inline-flex items-center gap-2 ${ready.length > 0 ? 'text-rose-900' : 'text-indigo-900'}`}>
            {ready.length > 0 && (
              <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
              </span>
            )}
            🔥 Ready for Quotation · <span className="font-mono">{ready.length}</span>
          </h2>
          <span className={`text-[11px] ${ready.length > 0 ? 'text-rose-800 font-bold' : 'text-indigo-700'}`}>
            {ready.length > 0
              ? 'إشعار عاجل — الأسعار جاهزة. أنشئ عرض السعر للعميل الآن.'
              : 'مقارنات مكتملة الموافقة وجاهزة للتحويل إلى عرض سعر للعميل'}
          </span>
        </div>
        {ready.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="ready-empty">
            لا توجد مقارنات معتمدة بانتظار إنشاء عرض. عند اكتمال Procurement + Finance Approval ستظهر هنا.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white text-gray-600 text-xs">
              <tr>
                <th className="text-left px-3 py-2">Comparison</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Awarded Supplier</th>
                <th className="text-right px-3 py-2">Items</th>
                <th className="text-right px-3 py-2">Subtotal</th>
                <th className="px-3 py-2 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {ready.map((r) => (
                <tr key={r.comparison_id}
                     className="border-t border-rose-100 bg-rose-50/30 hover:bg-rose-100/50 cursor-pointer"
                     data-testid={`ready-row-${r.ref_no}`}>
                  <td className="px-3 py-2 font-mono text-rose-800 font-semibold">{r.ref_no}</td>
                  <td className="px-3 py-2 font-medium">{r.customer_name || '—'}</td>
                  <td className="px-3 py-2">{r.project_name || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.awarded_supplier || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.items_count}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">{(r.subtotal || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openFromComparison(r.comparison_id)}
                            disabled={opening === r.comparison_id}
                            data-testid={`ready-create-${r.ref_no}`}
                            className="text-xs px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:bg-gray-300">
                      {opening === r.comparison_id ? '…' : '🔥 Create Quotation →'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}

      {/* ──── My Quotations (bottom section) ──── */}
      <section className="border border-gray-200 rounded-lg overflow-hidden"
                data-testid="sales-quotations-list-section">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900 inline-flex items-center gap-2">
            📑 My Quotations · <span className="font-mono">{quotes.length}</span>
          </h2>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : quotes.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="quotes-empty">
            لم يتم إنشاء أي عروض بعد. ابدأ من قائمة &ldquo;Ready for Quotation&rdquo; أعلاه.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs">
              <tr>
                <th className="text-left px-3 py-2">Quote #</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Quote Date</th>
                <th className="text-left px-3 py-2">Expiry</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2 w-40">Quick Action</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} onClick={() => openDetail(q.id)}
                     className="border-t border-gray-100 hover:bg-indigo-50/30 cursor-pointer"
                     data-testid={`quote-row-${q.quote_no}`}>
                  <td className="px-3 py-2 font-mono text-indigo-700 font-semibold">
                    {q.quote_no}
                    {(q.version || 1) > 1 && (
                      <span className="ml-1 text-[9px] px-1 rounded bg-purple-100 text-purple-800 font-semibold align-middle">
                        v{q.version}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{q.customer_name || '—'}</td>
                  <td className="px-3 py-2">{q.project_name || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{q.quote_date || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{q.expiry_date || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{(q.total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[q.status] || STATUS_TONE.draft}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {q.status === 'draft' && (
                      <button onClick={() => submitFromRow(q.id)}
                              data-testid={`quote-row-submit-${q.quote_no}`}
                              className="text-[11px] px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold inline-flex items-center gap-1">
                        <Send className="w-3 h-3" /> Submit
                      </button>
                    )}
                    {q.status === 'pending_sales_review' && (
                      <span className="text-[10px] text-amber-700 italic">Awaiting approval</span>
                    )}
                    {q.status === 'sent_to_customer' && (
                      <button onClick={() => markAcceptedFromRow(q.id)}
                              data-testid={`quote-row-accept-${q.quote_no}`}
                              className="text-[11px] px-2.5 py-1 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-semibold inline-flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" /> Accept
                      </button>
                    )}
                    {q.status === 'customer_accepted' && !q.contract_id && (
                      <button onClick={() => createContractFromRow(q.id)}
                              data-testid={`quote-row-create-contract-${q.quote_no}`}
                              className="text-[11px] px-2.5 py-1 rounded bg-indigo-700 hover:bg-indigo-800 text-white font-semibold inline-flex items-center gap-1">
                        <FileSignature className="w-3 h-3" /> Create Contract
                      </button>
                    )}
                    {q.status === 'customer_accepted' && q.contract_id && (
                      <span data-testid={`quote-row-contract-linked-${q.quote_no}`}
                            className="text-[10px] text-emerald-800 font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                        <CheckCircle className="w-3 h-3" /> {q.contract_number}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {viewing && (
        <QuoteDetailModal quote={viewing} approvalCfg={approvalCfg}
                           onClose={() => setViewing(null)}
                           onRefresh={() => refreshDetail(viewing.id)} />
      )}
    </div>
  );
}


// ────────────────────────── Detail / Edit modal ───────────────────────
function QuoteDetailModal({ quote, approvalCfg, onClose, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [reopenMode, setReopenMode] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  // Editable form state.
  const [form, setForm] = useState(() => ({
    expiry_date:    quote.expiry_date,
    payment_terms:  quote.payment_terms || '',
    delivery_time:  quote.delivery_time || '',
    notes:          quote.notes || '',
    tax_rate:       quote.tax_rate ?? 15,
    lines:          (quote.lines || []).map((l) => ({ ...l })),
  }));

  const status = quote.status;
  // Edit is only allowed in states the backend won't reject. customer_accepted
  // is intentionally LOCKED — use Reopen to unlock it first.
  const canEdit = !['rejected', 'cancelled', 'customer_accepted'].includes(status);
  const canApprove = approvalCfg?.can_approve;
  const sendLog = quote.send_log || {};
  const isRevisionLocked = ['approved', 'sent_to_employee', 'sent_to_customer', 'customer_accepted'].includes(status);
  const versions = quote.versions || [];
  const [activeTab, setActiveTab] = useState('details');

  const recomputeTotals = (lines, taxRate) => {
    const sub = lines.reduce((a, l) => a + (Number(l.qty || 0) * Number(l.unit_price || 0)), 0);
    const tax = sub * Number(taxRate || 0) / 100;
    return { sub: round2(sub), tax: round2(tax), total: round2(sub + tax) };
  };
  const { sub, tax, total } = recomputeTotals(form.lines, form.tax_rate);

  const updateLine = (idx, patch) => setForm((f) => ({
    ...f,
    lines: f.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln),
  }));
  const addManualLine = () => setForm((f) => ({
    ...f,
    lines: [...f.lines, {
      no: f.lines.length + 1, source_item_no: null, description: '',
      qty: 1, unit: '', unit_price: 0, is_manual: true,
    }],
  }));
  const removeLine = (idx) => setForm((f) => ({
    ...f,
    lines: f.lines.filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setBusy(true);
    try {
      await axiosInstance.patch(`/sales-quotations/${quote.id}`, form);
      toast.success('Saved');
      setEditing(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  const action = async (verb, body = {}) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/sales-quotations/${quote.id}/${verb}`, body);
      toast.success(verb.replace(/-/g, ' '));
      // Submit is a hand-off — the next actor is a manager. Bounce the
      // user to /approvals so they can SEE that their work moved forward.
      if (verb === 'submit') {
        toast.message('Your manager has been notified. Opening /approvals…');
        setTimeout(() => { window.location.assign('/approvals'); }, 700);
        return;
      }
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail?.message || `${verb} failed`);
    } finally { setBusy(false); }
  };

  // Modal helper — spin up a Contract from this SQ (customer_accepted only).
  const createContractFromRow = async (id) => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/contracts/from-quotation/${id}`);
      toast.success(r.data?.idempotent
        ? `Already linked → ${r.data?.contract?.contract_number}`
        : `Contract ${r.data?.contract?.contract_number} created`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                || e.response?.data?.detail?.error
                || 'Failed to create Contract');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="quote-detail-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[94vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            <span className="font-mono text-indigo-700">{quote.quote_no}</span>
            {(quote.version || 1) > 1 && (
              <span data-testid="quote-version-badge"
                    className="text-[10px] px-2 py-0.5 rounded font-semibold bg-purple-100 text-purple-800 border border-purple-300">
                v{quote.version}
              </span>
            )}
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[status] || STATUS_TONE.draft}`}
                  data-testid="quote-detail-status">{status}</span>
            <span className="text-xs text-gray-400 ml-2">↩ {quote.source_ref_no}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        {/* Tabs */}
        <div className="px-4 border-b border-gray-200 bg-white flex items-center gap-1 sticky top-[57px] z-10">
          <button onClick={() => setActiveTab('details')}
                  data-testid="quote-tab-details"
                  className={`text-xs px-3 py-2 border-b-2 font-semibold ${activeTab === 'details' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            Details
          </button>
          <button onClick={() => setActiveTab('versions')}
                  data-testid="quote-tab-versions"
                  className={`text-xs px-3 py-2 border-b-2 font-semibold inline-flex items-center gap-1 ${activeTab === 'versions' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            <History className="w-3.5 h-3.5" /> Versions · {versions.length + 1}
          </button>
        </div>

        {activeTab === 'versions' && (
          <VersionsPanel currentQuote={quote} versions={versions} />
        )}

        {activeTab === 'details' && (
        <div className="p-4 space-y-4">
          {isRevisionLocked && editing && (
            <div data-testid="revision-warning-banner"
                 className="border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded inline-flex items-start gap-2">
              <History className="w-4 h-4 mt-0.5" />
              <div>
                <strong>تنبيه — تعديل عرض معتمد:</strong>
                {' '}أي تعديل على <code className="font-mono">Description</code> / <code className="font-mono">Qty</code> / <code className="font-mono">Unit Price (manual)</code> / <code className="font-mono">Payment Terms</code> / <code className="font-mono">Delivery Time</code> / إضافة/حذف بنود سيُنشئ نسخة جديدة (<strong>v{(quote.version || 1) + 1}</strong>) ويعيد العرض إلى <strong>Pending Approval</strong>. تعديلات مثل ملاحظات أو تاريخ الصلاحية لا تُولّد نسخة.
              </div>
            </div>
          )}
          {/* Header card */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm border border-gray-100 rounded p-3 bg-gray-50">
            <FieldRO label="Customer"  value={quote.customer_name} />
            <FieldRO label="Project"   value={quote.project_name} />
            <FieldRO label="Quote Date" value={quote.quote_date} />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Expiry Date</p>
              {editing ? (
                <input type="date" value={form.expiry_date || ''}
                        onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                        data-testid="quote-expiry-date"
                        className="text-sm font-medium border border-gray-300 rounded px-2 py-1 mt-0.5 w-full" />
              ) : (
                <p className="text-sm font-medium">{quote.expiry_date || '—'}</p>
              )}
            </div>
            <FieldRO label="Awarded Supplier" value={quote.awarded_supplier_name} />
            <FieldRO label="Type of Work" value={quote.type_of_work} />
            <FieldRO label="Created" value={quote.created_by_name} />
          </div>

          {/* Lines */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center justify-between">
              <span>📦 Items · {form.lines.length} line(s)</span>
              {editing && (
                <button onClick={addManualLine}
                        data-testid="quote-add-manual-line"
                        className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Manual Line
                </button>
              )}
            </div>
            <table className="w-full text-xs" data-testid="quote-lines-table">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-2 py-1 w-8">#</th>
                  <th className="text-left px-2 py-1">Description</th>
                  <th className="text-right px-2 py-1 w-16">Qty</th>
                  <th className="text-left px-2 py-1 w-20">Unit</th>
                  <th className="text-right px-2 py-1 w-24">Unit Price</th>
                  <th className="text-right px-2 py-1 w-24">Total</th>
                  <th className="px-2 py-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => {
                  const lineTotal = round2(Number(ln.qty || 0) * Number(ln.unit_price || 0));
                  const locked = !ln.is_manual; // award-locked supplier line
                  return (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="px-2 py-1 text-gray-400">
                        {i + 1}
                        {locked && <span className="block text-[9px] text-rose-600">🔒 award</span>}
                        {!locked && <span className="block text-[9px] text-emerald-600">+ manual</span>}
                      </td>
                      <td className="px-2 py-1">
                        {editing ? (
                          <textarea rows={2} value={ln.description || ''}
                                     onChange={(e) => updateLine(i, { description: e.target.value })}
                                     data-testid={`quote-line-${i}-description`}
                                     className="w-full text-xs border border-gray-300 rounded px-1.5 py-1" />
                        ) : (
                          <div>{ln.description || '—'}</div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {editing ? (
                          <input type="number" value={ln.qty} step="any"
                                  onChange={(e) => updateLine(i, { qty: e.target.value })}
                                  data-testid={`quote-line-${i}-qty`}
                                  className="w-16 text-right text-xs border border-gray-300 rounded px-1.5 py-0.5" />
                        ) : (
                          <span className="font-mono">{ln.qty}</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {editing ? (
                          <input type="text" value={ln.unit || ''}
                                  onChange={(e) => updateLine(i, { unit: e.target.value })}
                                  data-testid={`quote-line-${i}-unit`}
                                  className="w-16 text-xs border border-gray-300 rounded px-1.5 py-0.5" />
                        ) : (
                          <span className="text-gray-600">{ln.unit || '—'}</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {editing ? (
                          <input type="number" value={ln.unit_price} step="any" disabled={locked}
                                  onChange={(e) => updateLine(i, { unit_price: e.target.value })}
                                  data-testid={`quote-line-${i}-unit-price`}
                                  title={locked ? 'Award-locked — cannot change' : ''}
                                  className={`w-20 text-right text-xs border rounded px-1.5 py-0.5 ${locked ? 'bg-rose-50 border-rose-200 text-rose-700 cursor-not-allowed' : 'border-gray-300'}`} />
                        ) : (
                          <span className="font-mono">{ln.unit_price}</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700">
                        {lineTotal.toFixed(2)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {editing && !locked && (
                          <button onClick={() => removeLine(i)}
                                  data-testid={`quote-line-${i}-remove`}
                                  className="text-rose-600 hover:text-rose-800">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                  <td colSpan={5} className="px-2 py-1 text-right">Subtotal</td>
                  <td className="px-2 py-1 text-right font-mono">{sub.toFixed(2)}</td>
                  <td></td>
                </tr>
                <tr className="bg-gray-50">
                  <td colSpan={5} className="px-2 py-1 text-right">
                    VAT ({editing ? (
                      <input type="number" value={form.tax_rate} step="0.5"
                              onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                              data-testid="quote-tax-rate"
                              className="w-14 text-right text-xs border border-gray-300 rounded px-1.5 py-0.5 ml-1" />
                    ) : `${form.tax_rate}`}%)
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{tax.toFixed(2)}</td>
                  <td></td>
                </tr>
                <tr className="bg-emerald-50 font-bold border-t border-emerald-200">
                  <td colSpan={5} className="px-2 py-1 text-right text-emerald-900">TOTAL</td>
                  <td className="px-2 py-1 text-right font-mono text-emerald-900">{total.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Commercial fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Payment Terms</label>
              {editing ? (
                <textarea rows={2} value={form.payment_terms}
                           onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                           data-testid="quote-payment-terms"
                           className="w-full text-sm border border-gray-300 rounded px-2 py-1 mt-0.5" />
              ) : (
                <p className="text-sm whitespace-pre-line">{quote.payment_terms || '—'}</p>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Delivery Time</label>
              {editing ? (
                <input type="text" value={form.delivery_time}
                        onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                        data-testid="quote-delivery-time"
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1 mt-0.5" />
              ) : (
                <p className="text-sm">{quote.delivery_time || '—'}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Notes</label>
              {editing ? (
                <textarea rows={2} value={form.notes}
                           onChange={(e) => setForm({ ...form, notes: e.target.value })}
                           data-testid="quote-notes"
                           className="w-full text-sm border border-gray-300 rounded px-2 py-1 mt-0.5" />
              ) : (
                <p className="text-sm whitespace-pre-line">{quote.notes || '—'}</p>
              )}
            </div>
          </div>

          {/* Send log */}
          {(sendLog.employee || sendLog.customer) && (
            <div className="text-xs space-y-1 border border-gray-100 rounded p-2 bg-gray-50">
              {sendLog.employee && (
                <div data-testid="quote-send-log-employee">
                  📨 Sent to employee <strong>{sendLog.employee.to}</strong>
                  (CC: {(sendLog.employee.cc || []).join(', ')})
                  · {sendLog.employee.sent_at && new Date(sendLog.employee.sent_at).toLocaleString()}
                  {sendLog.employee.delivered === false &&
                    <span className="ml-2 text-amber-700">⚠ {sendLog.employee.error || 'not delivered'}</span>}
                </div>
              )}
              {sendLog.customer && (
                <div data-testid="quote-send-log-customer">
                  📤 Sent to customer
                  {sendLog.customer.method === 'manual'
                    ? <> <strong>Manual</strong> ({sendLog.customer.notes})</>
                    : <> <strong>Email</strong> to {sendLog.customer.to}</>}
                  · {sendLog.customer.sent_at && new Date(sendLog.customer.sent_at).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Audit trail */}
          {(quote.history || []).length > 0 && (
            <details>
              <summary className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold cursor-pointer">
                Audit Trail · {(quote.history || []).length} event(s)
              </summary>
              <ul className="text-xs text-gray-700 mt-2 space-y-1">
                {quote.history.map((h, i) => (
                  <li key={i} className="inline-flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-gray-400">{h.at && new Date(h.at).toLocaleString()}</span>
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] uppercase">{h.action}</span>
                    <span className="text-gray-500">by</span>
                    <strong>{h.user_name || '—'}</strong>
                    {h.notes && <span className="text-gray-500">— {h.notes}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        )}

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2 flex-wrap sticky bottom-0">
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} disabled={busy}
                      className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={save} disabled={busy}
                      data-testid="quote-save"
                      className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
                💾 Save Changes
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button onClick={() => setEditing(true)} disabled={busy}
                        data-testid="quote-edit"
                        className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 font-semibold">
                  ✏️ Edit
                </button>
              )}
              {status === 'draft' && (
                <button onClick={() => action('submit')} disabled={busy}
                        data-testid="quote-submit"
                        className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
                  Submit for Sales Approval →
                </button>
              )}
              {status === 'pending_sales_review' && (
                canApprove ? (
                  <>
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mr-auto">
                      Manager Action Required
                    </span>
                    <button onClick={() => setRejectMode(true)} disabled={busy}
                            data-testid="quote-reject"
                            className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold">
                      ✗ Reject
                    </button>
                    <button onClick={() => action('approve')} disabled={busy}
                            data-testid="quote-approve"
                            className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                      ✓ Approve
                    </button>
                  </>
                ) : (
                  // Non-approver view (e.g. sales rep) — show clear status
                  // banner with a link to /approvals where the manager will act.
                  <div className="flex items-center justify-between w-full">
                    <span className="text-xs text-amber-900 font-semibold inline-flex items-center gap-1.5"
                           data-testid="quote-awaiting-approval-banner">
                      ⏳ Submitted — Awaiting Manager Approval
                    </span>
                    <a href="/approvals"
                       className="text-xs px-3 py-1.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-50 font-semibold no-underline">
                      View in Approvals →
                    </a>
                  </div>
                )
              )}
              {status === 'approved' && (
                <button onClick={() => action('send-to-employee', {})} disabled={busy}
                        data-testid="quote-send-to-employee"
                        className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold inline-flex items-center gap-1">
                  <Send className="w-3 h-3" /> Send to Employee (CC: aghiad)
                </button>
              )}
              {status === 'sent_to_employee' && (
                <button onClick={() => setShowSend(true)} disabled={busy}
                        data-testid="quote-send-to-customer"
                        className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                  📤 Send to Customer ▾
                </button>
              )}
              {status === 'sent_to_customer' && (
                <>
                  <span className="text-xs text-emerald-800 font-semibold inline-flex items-center gap-1"
                         data-testid="quote-sent-banner">
                    <CheckCircle className="w-4 h-4" /> Sent to customer
                  </span>
                  <button onClick={() => action('mark-accepted')} disabled={busy}
                          data-testid="quote-mark-accepted"
                          className="text-xs px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-semibold inline-flex items-center gap-1">
                    <ThumbsUp className="w-3.5 h-3.5" /> Mark as Customer-Accepted
                  </button>
                </>
              )}
              {status === 'customer_accepted' && (
                <>
                  {quote.contract_id ? (
                    <span className="text-xs text-emerald-900 font-bold inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded"
                           data-testid="quote-contract-linked-banner">
                      <FileSignature className="w-4 h-4" /> Linked Contract: {quote.contract_number}
                    </span>
                  ) : (
                    <>
                      <span className="text-xs text-emerald-900 font-bold inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded"
                             data-testid="quote-final-banner">
                        <ThumbsUp className="w-4 h-4" /> Customer Accepted · Locked · Ready for Contract
                      </span>
                      <button onClick={() => createContractFromRow(quote.id)} disabled={busy}
                              data-testid="quote-create-contract"
                              className="text-xs px-3 py-1.5 rounded bg-indigo-700 hover:bg-indigo-800 text-white font-semibold inline-flex items-center gap-1">
                        <FileSignature className="w-3.5 h-3.5" /> Create Contract →
                      </button>
                    </>
                  )}
                  {(approvalCfg?.can_reopen ?? true) && (
                    <button onClick={() => setReopenMode(true)} disabled={busy}
                            data-testid="quote-reopen"
                            className="text-xs px-3 py-1.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-50 font-semibold inline-flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> Reopen…
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Reject inline form */}
        {rejectMode && (
          <div className="px-4 py-3 border-t border-rose-200 bg-rose-50">
            <textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                       rows={2} placeholder="سبب الرفض (إجباري)"
                       data-testid="quote-reject-notes"
                       className="w-full text-xs border border-rose-200 rounded p-2 bg-white" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setRejectMode(false); setRejectNotes(''); }}
                      className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={() => { action('reject', { notes: rejectNotes }); setRejectMode(false); setRejectNotes(''); }}
                      disabled={busy || !rejectNotes.trim()}
                      data-testid="quote-reject-confirm"
                      className="text-xs px-3 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:bg-gray-300 font-semibold">
                Confirm Reject
              </button>
            </div>
          </div>
        )}

        {/* Reopen inline form — admin/manager only */}
        {reopenMode && (
          <div className="px-4 py-3 border-t border-amber-300 bg-amber-50" data-testid="quote-reopen-form">
            <div className="text-[11px] uppercase tracking-wider text-amber-900 font-bold mb-1">
              Reopen a Customer-Accepted Quote
            </div>
            <p className="text-[11px] text-amber-800 mb-2">
              سيتم إنشاء نسخة جديدة (v{(quote.version || 1) + 1}) وإعادة العرض إلى Pending Approval. سبب إعادة الفتح إجباري ويُحفظ في سجل التدقيق.
            </p>
            <textarea value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
                       rows={2} placeholder="مثلاً: العميل طلب تعديل المواصفات بعد القبول"
                       data-testid="quote-reopen-reason"
                       className="w-full text-xs border border-amber-300 rounded p-2 bg-white" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setReopenMode(false); setReopenReason(''); }}
                      className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={() => { action('reopen', { reason: reopenReason }); setReopenMode(false); setReopenReason(''); }}
                      disabled={busy || !reopenReason.trim()}
                      data-testid="quote-reopen-confirm"
                      className="text-xs px-3 py-1 rounded bg-amber-700 hover:bg-amber-800 text-white disabled:bg-gray-300 font-semibold">
                Confirm Reopen
              </button>
            </div>
          </div>
        )}

        {showSend && (
          <SendToCustomerSheet quote={quote}
                                onClose={() => setShowSend(false)}
                                onSent={() => { setShowSend(false); onRefresh && onRefresh(); }} />
        )}
      </div>
    </div>
  );
}


function SendToCustomerSheet({ quote, onClose, onSent }) {
  const [method, setMethod] = useState('email');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (method === 'email' && !customerEmail.includes('@')) {
      toast.error('Customer email required'); return;
    }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/sales-quotations/${quote.id}/send-to-customer`,
                                          { method, customer_email: customerEmail || null, notes: notes || null });
      if (method === 'manual') toast.success('Marked as sent');
      else {
        const ok = r.data?.send_log?.customer?.delivered;
        toast.success(ok ? `📧 Sent to ${customerEmail}` : 'Status updated · email service unavailable');
      }
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Send failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="send-customer-sheet" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold">📤 Send to Customer · {quote.quote_no}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMethod('email')}
                     data-testid="send-customer-method-email"
                     className={`px-3 py-2 rounded border text-sm font-semibold ${method === 'email' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              📧 Send by Email
            </button>
            <button onClick={() => setMethod('manual')}
                     data-testid="send-customer-method-manual"
                     className={`px-3 py-2 rounded border text-sm font-semibold ${method === 'manual' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              ✋ Mark as Sent
            </button>
          </div>
          {method === 'email' && (
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-semibold">Customer Email *</label>
              <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="customer@example.com"
                      data-testid="send-customer-email-input"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-semibold">
              {method === 'manual' ? 'How was it sent? (notes)' : 'Optional cover note'}
            </label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder={method === 'manual' ? 'مثلاً: WhatsApp 0555... أو نسخة مطبوعة' : 'Optional message'}
                       data-testid="send-customer-notes"
                       className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="send-customer-submit"
                  className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:bg-gray-300">
            {method === 'email' ? '📧 Send Now' : '✓ Mark as Sent'}
          </button>
        </div>
      </div>
    </div>
  );
}


function FieldRO({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-sm font-medium">{value || '—'}</p>
    </div>
  );
}

function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }


// ────────────────────────── Versions Panel ───────────────────────
function VersionsPanel({ currentQuote, versions }) {
  // Build a unified list: archived versions in order + current (live) version on top
  const liveVersion = {
    version:        currentQuote.version || 1,
    status_at_snapshot: currentQuote.status,
    snapshot: {
      payment_terms: currentQuote.payment_terms,
      delivery_time: currentQuote.delivery_time,
      tax_rate:      currentQuote.tax_rate,
      subtotal:      currentQuote.subtotal,
      tax_amount:    currentQuote.tax_amount,
      total:         currentQuote.total,
      lines:         currentQuote.lines || [],
      notes:         currentQuote.notes,
      expiry_date:   currentQuote.expiry_date,
    },
    revised_at:      currentQuote.updated_at,
    revised_by_name: currentQuote.created_by_name,
    is_live:         true,
  };
  const allVersions = [liveVersion, ...[...versions].reverse()];
  const [openVersion, setOpenVersion] = useState(null);

  return (
    <div className="p-4 space-y-3" data-testid="versions-panel">
      <div className="text-xs text-gray-600">
        كل تعديل على حقل حساس (Description / Qty / Unit Price يدوي / Payment Terms / Delivery Time / إضافة-حذف بند) بعد الاعتماد يُولّد نسخة جديدة ويعيد العرض إلى Pending Approval.
      </div>
      <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
        <thead className="bg-gray-50 text-gray-600 text-xs">
          <tr>
            <th className="text-left px-3 py-2 w-20">Version</th>
            <th className="text-left px-3 py-2">Status When Saved</th>
            <th className="text-left px-3 py-2">Revised By</th>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Changes</th>
            <th className="text-right px-3 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {allVersions.map((v) => (
            <tr key={v.version + (v.is_live ? '-live' : '')}
                 className={`border-t border-gray-100 ${v.is_live ? 'bg-indigo-50/30' : ''}`}
                 data-testid={`version-row-${v.version}${v.is_live ? '-live' : ''}`}>
              <td className="px-3 py-2 font-mono font-semibold text-indigo-700">
                v{v.version}{v.is_live && <span className="ml-1 text-[10px] text-emerald-700">(current)</span>}
              </td>
              <td className="px-3 py-2">
                <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[v.status_at_snapshot] || STATUS_TONE.draft}`}>
                  {v.status_at_snapshot}
                </span>
              </td>
              <td className="px-3 py-2">{v.revised_by_name || '—'}</td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {v.revised_at && new Date(v.revised_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 text-xs">
                {v.is_live ? <em className="text-gray-400">live</em>
                  : (v.changes || []).length === 0 ? <em className="text-gray-400">—</em>
                  : <span>{v.changes.length} change(s)</span>}
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => setOpenVersion(v)}
                        data-testid={`view-version-${v.version}${v.is_live ? '-live' : ''}`}
                        className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {openVersion && (
        <VersionSnapshotModal v={openVersion} quoteNo={currentQuote.quote_no}
                               onClose={() => setOpenVersion(null)} />
      )}
    </div>
  );
}


function VersionSnapshotModal({ v, quoteNo, onClose }) {
  const lines = v.snapshot?.lines || [];
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
          data-testid={`version-snapshot-${v.version}`} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold inline-flex items-center gap-2">
            <History className="w-4 h-4 text-gray-500" />
            <span className="font-mono text-indigo-700">{quoteNo}</span>
            <span className="text-purple-800 font-bold">v{v.version}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[v.status_at_snapshot] || STATUS_TONE.draft}`}>
              {v.status_at_snapshot}
            </span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-3">
          {(v.changes || []).length > 0 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold mb-2">
                Differences vs the PRIOR version
              </h4>
              <ul className="text-xs space-y-1">
                {v.changes.map((c, i) => (
                  <li key={i} className="border border-gray-100 rounded px-2 py-1 bg-gray-50">
                    <span className="font-mono text-[10px] uppercase text-rose-700 mr-2">{c.kind || c.field}</span>
                    {c.line && <span className="text-gray-500 mr-2">[{c.line}]</span>}
                    <span className="text-gray-500">old:</span>
                    <code className="ml-1 mr-2">{String(c.old ?? '∅')}</code>
                    <span className="text-gray-500">new:</span>
                    <code className="ml-1">{String(c.new ?? '∅')}</code>
                    {(c.description || c.qty || c.unit_price) && !c.old && !c.new && (
                      <span className="text-gray-600">— {c.description} qty:{c.qty} @ {c.unit_price}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h4 className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold mb-2">
              Snapshot — payment / delivery / totals
            </h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-gray-500">Payment Terms:</span> {v.snapshot?.payment_terms || '—'}</div>
              <div><span className="text-gray-500">Delivery Time:</span> {v.snapshot?.delivery_time || '—'}</div>
              <div><span className="text-gray-500">Subtotal:</span> {(v.snapshot?.subtotal || 0).toFixed(2)}</div>
              <div><span className="text-gray-500">VAT:</span> {(v.snapshot?.tax_amount || 0).toFixed(2)}</div>
              <div className="col-span-2 font-bold"><span className="text-gray-500">TOTAL:</span> {(v.snapshot?.total || 0).toFixed(2)}</div>
            </div>
          </div>
          {lines.length > 0 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold mb-2">
                Items ({lines.length})
              </h4>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-2 py-1 w-8">#</th>
                    <th className="text-left px-2 py-1">Description</th>
                    <th className="text-right px-2 py-1 w-16">Qty</th>
                    <th className="text-right px-2 py-1 w-24">Unit Price</th>
                    <th className="text-right px-2 py-1 w-24">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-1 text-gray-400">{ln.no || i + 1}</td>
                      <td className="px-2 py-1">{ln.description}</td>
                      <td className="px-2 py-1 text-right font-mono">{ln.qty}</td>
                      <td className="px-2 py-1 text-right font-mono">{ln.unit_price}</td>
                      <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700">
                        {(Number(ln.qty || 0) * Number(ln.unit_price || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
